import { LeadForm, SaveLeadFormDraftParams } from "@plura/shared/types";
import { TemplateInput } from "./templates/types";

/**
 * Converts SaveLeadFormDraftParams to a complete LeadForm object
 * This is useful for previewing templates before they are saved to the database
 * 
 * @param draftParams - The draft form parameters (usually from template inputData)
 * @param templateInput - The template input containing IDs for the form
 * @returns A complete LeadForm object that can be used for preview/testing
 */
export const convertDraftParamsToLeadForm = (
  draftParams: SaveLeadFormDraftParams,
  templateInput: TemplateInput
): LeadForm => {
  const now = new Date();

  return {
    // IDs and metadata from templateInput
    id: templateInput.formId,
    workspaceId: templateInput.workspaceId,
    collectionId: draftParams.collectionId,

    // Form status and type - defaults for templates
    status: 'DRAFT' as const,
    type: 'MULTI_STEP' as const,
    version: 1,

    // Style settings from draftParams
    fontSizeSetting: draftParams.fontSizeSetting,
    screenFontSizeSetting: draftParams.screenFontSizeSetting ?? null,
    transitionType: draftParams.transitionType,
    transitionSpeed: draftParams.transitionSpeed,
    elementType: draftParams.elementType,
    backgroundColorSetting: draftParams.backgroundColorSetting,
    textColorSetting: draftParams.textColorSetting,
    buttonColorSetting: draftParams.buttonColorSetting,
    buttonTextColorSetting: draftParams.buttonTextColorSetting,
    answerColorSetting: draftParams.answerColorSetting ?? null,
    fontFamilySetting: draftParams.fontFamilySetting,

    // Form content from draftParams
    name: draftParams.name ?? 'Untitled Form',
    overview: draftParams.overview ?? null,
    about: draftParams.about ?? null,
    tone: draftParams.tone ?? null,

    // Form settings from draftParams
    hideBuiltWithBadge: draftParams.hideBuiltWithBadge,
    closed: draftParams.closed,
    closedMessage: draftParams.closedMessage ?? null,
    legalDisclaimer: draftParams.legalDisclaimer,
    legalMessage: draftParams.legalMessage ?? null,
    notifications: draftParams.notifications,
    notificationEmail: draftParams.notificationEmail ?? null,
    logoUrl: draftParams.logoUrl ?? null,
    logoPosition: draftParams.logoPosition ?? 'TOP_LEFT',
    skipWelcomeScreen: draftParams.skipWelcomeScreen,
    showTimeToComplete: draftParams.showTimeToComplete ?? true,
    showAnswerLetters: draftParams.showAnswerLetters ?? true,
    customEndScreen: draftParams.customEndScreen ?? false,

    // Tracking and integrations from draftParams
    googleTagManager: draftParams.googleTagManager ?? null,
    metaPixel: draftParams.metaPixel ?? null,
    assistantId: draftParams.assistantId ?? null,

    // Metadata from draftParams
    metaTitle: draftParams.metaTitle ?? null,
    metaDescription: draftParams.metaDescription ?? null,
    metaImageUrl: draftParams.metaImageUrl ?? null,
    consentMessageTitle: draftParams.consentMessageTitle ?? null,

    // GDPR and privacy settings
    gdprSetting: draftParams.gdprSetting,
    gdprRequired: draftParams.gdprRequired,

    // Form data from draftParams - map journey items to ensure formId is set
    hiddenFields: draftParams.hiddenFields ?? null,
    journeyItems: draftParams.journeyItems?.map(item => ({
      ...item,
      formId: templateInput.formId,
      endOnCompletion: item.endOnCompletion ?? null,
      createdAt: item.createdAt instanceof Date ? item.createdAt : new Date(item.createdAt),
      updatedAt: item.updatedAt instanceof Date ? item.updatedAt : new Date(item.updatedAt),
      // Map logic items to handle dates and endOnCompletion
      logicItems: item.logicItems?.map(logic => ({
        ...logic,
        endOnCompletion: logic.endOnCompletion ?? null,
        createdAt: logic.createdAt instanceof Date ? logic.createdAt : new Date(logic.createdAt),
        updatedAt: logic.updatedAt instanceof Date ? logic.updatedAt : new Date(logic.updatedAt),
      })) ?? null,
      sourceLogicItems: item.sourceLogicItems?.map(logic => ({
        ...logic,
        endOnCompletion: logic.endOnCompletion ?? null,
        createdAt: logic.createdAt instanceof Date ? logic.createdAt : new Date(logic.createdAt),
        updatedAt: logic.updatedAt instanceof Date ? logic.updatedAt : new Date(logic.updatedAt),
      })) ?? null,
      targetLogicItems: item.targetLogicItems?.map(logic => ({
        ...logic,
        endOnCompletion: logic.endOnCompletion ?? null,
        createdAt: logic.createdAt instanceof Date ? logic.createdAt : new Date(logic.createdAt),
        updatedAt: logic.updatedAt instanceof Date ? logic.updatedAt : new Date(logic.updatedAt),
      })) ?? null,
    })) ?? null,
    snippets: draftParams.snippets ?? null,

    // Additional fields for LeadForm (not in SaveLeadFormDraftParams)
    zapierTargetUrl: null,
    numberOfResponses: null,
    plan: null,

    // Timestamps
    createdAt: now,
    updatedAt: now,
  };
};

